<template>
    <div class="form-builder q-pa-md">
        <!-- Loading overlay -->
        <q-inner-loading :showing="loading" label="Loading..." />

        <!-- Header -->
        <div class="row items-center q-mb-md">
            <q-btn flat icon="arrow_back" @click="$emit('back')" class="q-mr-sm" />
            <h4 class="q-ma-none">{{ isEditing ? 'Edit Form Definition' : 'Create New Form' }}</h4>
        </div>

        <!-- Form Metadata -->
        <q-card flat bordered class="q-mb-md">
            <q-card-section>
                <div class="text-h6 q-mb-sm">Form Details</div>
                <div class="q-gutter-sm">
                    <q-input
                        v-model="formData.title"
                        label="Form Title *"
                        filled
                        dense
                        :rules="[val => !!val || 'Title is required']"
                    />
                    <q-input
                        v-model="formData.description"
                        label="Form Description"
                        filled
                        dense
                        type="textarea"
                        autogrow
                    />
                </div>
            </q-card-section>
        </q-card>

        <!-- Pages -->
        <div class="q-mb-md">
            <div class="row items-center q-mb-sm">
                <div class="text-h6">Pages</div>
                <q-space />
                <q-btn
                    flat
                    no-caps
                    color="primary"
                    icon="add"
                    label="Add Page"
                    @click="addPage"
                />
            </div>

            <q-list v-if="formData.pages.length > 0" class="q-gutter-md">
                <q-expansion-item
                    v-for="(page, pageIndex) in formData.pages"
                    :key="pageIndex"
                    :label="`Page ${pageIndex + 1}: ${page.name || '(unnamed)'}`"
                    :caption="`${(page.fields || []).length} field(s)`"
                    header-class="bg-grey-2 text-primary"
                    default-opened
                    group="pages"
                >
                    <template v-slot:header>
                        <q-item-section avatar>
                            <q-icon name="description" color="primary" />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>Page {{ pageIndex + 1 }}: {{ page.name || '(unnamed)' }}</q-item-label>
                            <q-item-label caption>{{ (page.fields || []).length }} field(s) {{ page.description ? '— ' + page.description : '' }}</q-item-label>
                        </q-item-section>
                        <q-item-section side>
                            <div class="row q-gutter-xs">
                                <q-btn
                                    flat dense round icon="arrow_upward" size="sm"
                                    :disable="pageIndex === 0"
                                    @click.stop="movePage(pageIndex, -1)"
                                />
                                <q-btn
                                    flat dense round icon="arrow_downward" size="sm"
                                    :disable="pageIndex === formData.pages.length - 1"
                                    @click.stop="movePage(pageIndex, 1)"
                                />
                                <q-btn
                                    flat dense round icon="delete" color="negative" size="sm"
                                    @click.stop="removePage(pageIndex)"
                                />
                            </div>
                        </q-item-section>
                    </template>

                    <q-card flat>
                        <q-card-section>
                            <!-- Page metadata -->
                            <div class="row q-gutter-sm q-mb-md">
                                <q-input
                                    v-model="page.name"
                                    label="Page Name *"
                                    dense
                                    filled
                                    class="col"
                                    :rules="[val => !!val || 'Page name is required']"
                                />
                                <q-input
                                    v-model="page.description"
                                    label="Page Description"
                                    dense
                                    filled
                                    class="col"
                                />
                            </div>

                            <!-- Fields list -->
                            <div class="q-mb-sm text-subtitle2">Fields</div>
                            <div v-if="(page.fields || []).length === 0" class="text-grey-6 text-center q-pa-md">
                                No fields yet. Click "Add Field" to start building this page.
                            </div>

                            <FormBuilderFieldEditor
                                v-for="(field, fieldIndex) in page.fields"
                                :key="fieldIndex"
                                :field="field"
                                :field-types="fieldTypes"
                                :is-first="fieldIndex === 0"
                                :is-last="fieldIndex === page.fields.length - 1"
                                @update="(updated) => updateField(pageIndex, fieldIndex, updated)"
                                @move-up="moveField(pageIndex, fieldIndex, -1)"
                                @move-down="moveField(pageIndex, fieldIndex, 1)"
                                @remove="removeField(pageIndex, fieldIndex)"
                            />

                            <div class="row justify-center q-mt-sm">
                                <q-btn-dropdown
                                    flat
                                    no-caps
                                    color="primary"
                                    icon="add"
                                    label="Add Field"
                                >
                                    <q-list>
                                        <q-item
                                            v-for="ft in fieldTypes"
                                            :key="ft.id"
                                            clickable
                                            v-close-popup
                                            @click="addField(pageIndex, ft.id)"
                                        >
                                            <q-item-section>
                                                <q-item-label>{{ ft.id }}</q-item-label>
                                            </q-item-section>
                                        </q-item>
                                    </q-list>
                                </q-btn-dropdown>
                            </div>
                        </q-card-section>
                    </q-card>
                </q-expansion-item>
            </q-list>

            <div v-else class="text-grey-6 text-center q-pa-lg">
                No pages yet. Click "Add Page" to get started.
            </div>
        </div>

        <!-- Error messages -->
        <q-banner v-if="errorMessage" class="bg-negative text-white q-mb-md">
            <template v-slot:avatar><q-icon name="error" /></template>
            {{ errorMessage }}
            <template v-slot:action>
                <q-btn flat dense icon="close" size="sm" @click="errorMessage = null" />
            </template>
        </q-banner>

        <!-- Action buttons -->
        <div class="row justify-end q-gutter-sm q-mt-md">
            <q-btn
                flat
                no-caps
                label="Cancel"
                @click="$emit('back')"
            />
            <q-btn
                unelevated
                no-caps
                color="primary"
                :label="isEditing ? 'Save Changes' : 'Save Draft'"
                :loading="saving"
                @click="saveForm"
            />
        </div>

        <!-- Confirm delete page dialog -->
        <q-dialog v-model="confirmDeletePage">
            <q-card>
                <q-card-section>
                    <div class="text-h6">Delete Page</div>
                    <div class="q-mt-sm">
                        Are you sure you want to delete "{{ pageToDelete?.name || 'this page' }}" and all its fields?
                    </div>
                </q-card-section>
                <q-card-actions align="right">
                    <q-btn flat label="Cancel" v-close-popup />
                    <q-btn flat color="negative" label="Delete" @click="confirmRemovePage" v-close-popup />
                </q-card-actions>
            </q-card>
        </q-dialog>
    </div>
</template>

<script>
import formsApi from "@/services/api/forms";
import FormBuilderFieldEditor from "./FormBuilderFieldEditor.vue";

export default {
    name: "FormBuilder",
    components: { FormBuilderFieldEditor },
    props: {
        /** Pass an existing form ID to load and edit */
        editFormId: { type: Number, default: null },
    },
    emits: ['back', 'saved'],
    data() {
        return {
            loading: false,
            saving: false,
            errorMessage: null,
            fieldTypes: [],
            formData: {
                title: '',
                description: '',
                pages: [],
            },
            confirmDeletePage: false,
            pageToDelete: null,
            pageToDeleteIndex: null,
        };
    },
    computed: {
        isEditing() {
            return this.editFormId !== null;
        },
    },
    async mounted() {
        await this.loadFieldTypes();
        if (this.isEditing) {
            await this.loadExistingForm();
        }
    },
    methods: {
        async loadFieldTypes() {
            try {
                const response = await formsApi.getFieldTypes();
                const data = response.data?.data || response.data;
                this.fieldTypes = (Array.isArray(data) ? data : []).map(ft => ({
                    id: ft.id,
                    options: typeof ft.options === 'string' ? JSON.parse(ft.options) : (ft.options || {}),
                }));
            } catch (error) {
                console.error("Failed to load field types:", error);
                this.errorMessage = "Could not load field types.";
            }
        },
        async loadExistingForm() {
            this.loading = true;
            try {
                const response = await formsApi.getDynamicForm(this.editFormId);
                const form = response.data?.data || response.data;
                this.formData.title = form.title || '';
                this.formData.description = form.description || '';
                this.formData.xlsxImport = form.xlsxImport;
                this.formData.serviceName = form.serviceName;
                this.formData.pages = (form.pages || []).map(page => ({
                    id: page.id,
                    name: page.name || '',
                    description: page.description || '',
                    fields: (page.fields || []).map(field => ({
                        id: field.id,
                        typeId: field.fieldType,
                        label: field.label || '',
                        sublabel: field.sublabel || '',
                        required: !!field.required,
                        isStatus: !!field.isStatus,
                        goalId: field.goalId || null,
                        allowOptOut: field.allowOptOut,
                        xlsxHeaders: field.xlsxHeaders,
                        options: Object.fromEntries(Object.entries(field.options || {}).filter(([key]) => key !== 'suggestions')),
                    })),
                }));
            } catch (error) {
                console.error("Failed to load form:", error);
                this.errorMessage = "Could not load form definition.";
            } finally {
                this.loading = false;
            }
        },
        getDefaultOptions(typeId) {
            const ft = this.fieldTypes.find(t => t.id === typeId);
            return ft ? JSON.parse(JSON.stringify(ft.options)) : {};
        },
        addPage() {
            this.formData.pages.push({
                name: '',
                description: '',
                fields: [],
            });
        },
        removePage(index) {
            this.pageToDelete = this.formData.pages[index];
            this.pageToDeleteIndex = index;
            this.confirmDeletePage = true;
        },
        confirmRemovePage() {
            if (this.pageToDeleteIndex !== null) {
                this.formData.pages.splice(this.pageToDeleteIndex, 1);
            }
            this.pageToDelete = null;
            this.pageToDeleteIndex = null;
        },
        movePage(index, direction) {
            const newIndex = index + direction;
            if (newIndex < 0 || newIndex >= this.formData.pages.length) return;
            const item = this.formData.pages.splice(index, 1)[0];
            this.formData.pages.splice(newIndex, 0, item);
        },
        moveField(pageIndex, fieldIndex, direction) {
            const newIndex = fieldIndex + direction;
            const fields = this.formData.pages[pageIndex].fields;
            if (newIndex < 0 || newIndex >= fields.length) return;
            const item = fields.splice(fieldIndex, 1)[0];
            fields.splice(newIndex, 0, item);
        },
        addField(pageIndex, typeId) {
            this.formData.pages[pageIndex].fields.push({
                id: '',
                typeId,
                label: '',
                sublabel: '',
                required: false,
                isStatus: false,
                allowOptOut: true,
                goalId: null,
                options: this.getDefaultOptions(typeId),
            });
        },
        updateField(pageIndex, fieldIndex, updatedField) {
            this.formData.pages[pageIndex].fields[fieldIndex] = updatedField;
        },
        removeField(pageIndex, fieldIndex) {
            this.formData.pages[pageIndex].fields.splice(fieldIndex, 1);
        },
        validate() {
            if (!this.formData.title?.trim()) {
                this.errorMessage = "Form title is required.";
                return false;
            }
            if (this.formData.pages.length === 0) {
                this.errorMessage = "At least one page is required.";
                return false;
            }
            for (let i = 0; i < this.formData.pages.length; i++) {
                if (!this.formData.pages[i].name?.trim()) {
                    this.errorMessage = `Page ${i + 1} must have a name.`;
                    return false;
                }
            }
            return true;
        },
        async saveForm() {
            this.errorMessage = null;
            if (!this.validate()) return;

            this.saving = true;
            try {
                if (this.isEditing) {
                    await formsApi.updateFormDefinition(this.editFormId, this.formData);
                } else {
                    await formsApi.createFormDefinition(this.formData);
                }
                this.$emit('saved');
            } catch (error) {
                console.error("Failed to save form:", error);
                this.errorMessage = error.response?.data?.message || "Error saving form definition.";
            } finally {
                this.saving = false;
            }
        },
    },
};
</script>

<style scoped>
.form-builder {
    max-width: 1000px;
    margin: 0 auto;
}
</style>
